import React, { useCallback, useContext, useEffect, useState } from "react";

import { HistoryContext, useHistory } from "fond/history";
import { getHotkeyLabel, useHotkeys } from "fond/hooks/useHotkeys";
import { MapContext } from "fond/map/MapProvider";
import { useLayerEditable } from "fond/map/Toolbar";
import { IButtonProps } from "fond/map/Toolbar/Toolbar";
import ToolbarButton from "fond/map/Toolbar/ToolbarButton";

import { SinglePhase } from "./phaseNetwork";

interface IProps extends IButtonProps {
  phase: SinglePhase;
}

/**
 * Sets the Phase of all selected phase override points to the button's phase.
 */
const PhaseButton: React.FC<IProps> = ({ phase, title }) => {
  const { map, drawControl } = useContext(MapContext);
  const { set: setHistory } = useHistory(HistoryContext);
  const [hasSelection, setHasSelection] = useState(false);
  const [commonPhase, setCommonPhase] = useState<unknown>(null);
  const isLayerEditable = useLayerEditable();

  /**
   * If the user is selecting a single feature (or a set of features that all share
   * the same phase), then highlight the button for that phase. If the user is selecting multiple features
   * with different phases, then nothing is highlighted.
   */
  const handleOnSelect = useCallback(() => {
    const features = drawControl.current?.getSelected().features ?? [];
    setHasSelection(features.length > 0);
    const phases = new Set(features.map((feature) => feature.properties?.Phase));
    setCommonPhase(phases.size === 1 ? [...phases][0] : null);
  }, [drawControl]);

  useEffect(() => {
    map?.on("draw.selectionchange", handleOnSelect);

    return () => {
      map?.off("draw.selectionchange", handleOnSelect);
    };
  }, [map, handleOnSelect]);

  const handleOnClick = useCallback(() => {
    const selectedIds = drawControl.current?.getSelectedIds() ?? [];
    if (selectedIds.length === 0) return;

    selectedIds.forEach((id) => {
      drawControl.current?.setFeatureProperty(id, "Phase", phase);
    });

    const allFeatures = drawControl.current?.getAll();
    if (allFeatures) {
      setHistory(allFeatures);
    }

    // ensure the other toolbar buttons are updated to reflect the new phase of the selected features
    map?.fire("draw.selectionchange", { type: "draw.selectionchange", features: drawControl.current?.getSelected().features ?? [], points: [] });
  }, [drawControl, map, phase, setHistory]);

  useHotkeys({
    action: `PHASE_${phase}`,
    callback: (event) => {
      event.preventDefault();
      handleOnClick();
    },
    enabled: hasSelection && isLayerEditable,
  });

  const tooltipTitle = `${title ?? `Set phase ${phase}`} (${getHotkeyLabel(`PHASE_${phase}`)})`;

  return (
    <ToolbarButton
      title={tooltipTitle}
      onClick={handleOnClick}
      disabled={!hasSelection || !isLayerEditable}
      active={commonPhase === phase}
      data-testid={`toolbar-phase-${phase.toLowerCase()}-button`}
    >
      {phase}
    </ToolbarButton>
  );
};

export default PhaseButton;
